"use client";
import {
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import { isArray } from "lodash";
import { useMemo, useState } from "react";
import { TbArrowsSort } from "react-icons/tb";

interface SortingActionProps<T> {
  handleSort: ({ ordering }: { ordering: string }) => void;
  columnSchema?: ColumnDef<T>[];
}

function TableSort<TData>({
  columnSchema = [],
  handleSort,
}: SortingActionProps<TData>) {
  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc">("desc");
  const [selectedSortingField, setSelectedSortingField] =
    useState<string>("created_at");

  const sortingOptions: ColumnDef<TData>[] = useMemo(
    () => [
      { id: "created_at", accessorKey: "created_at", header: "Created" },
      { id: "updated_at", accessorKey: "updated_at", header: "Updated" },
      ...columnSchema.flatMap((c) => ("columns" in c ? c.columns || [] : [c])),
    ],
    [columnSchema],
  );

  return (
    <Menu closeOnSelect={false}>
      <Tooltip label="Sort" zIndex={11} hasArrow gutter={8} placement="top">
        <MenuButton
          as={IconButton}
          aria-label="Options"
          icon={<TbArrowsSort />}
          variant="outline"
          size={"sm"}
          colorScheme="gray"
          boxShadow={"md"}
        />
      </Tooltip>
      <MenuList zIndex={11}>
        <Text size={"md"} mx={2} fontWeight={600}>
          Sort By
        </Text>
        <MenuDivider />
        {sortingOptions.length > 0 &&
          isArray(sortingOptions) &&
          sortingOptions
            .filter(({ id }) => id !== "actions" && id !== "select")
            .map(({ id, header }) => (
              <MenuItem key={id}>
                <RadioGroup
                  value={selectedSortingField}
                  onChange={(val) => {
                    setSelectedSortingField(val);
                    handleSort({
                      ordering: `${sortingOrder === "asc" ? "" : "-"}${val}`,
                    });
                  }}
                >
                  <Radio value={id}>
                    <>{(header as React.ReactNode) || ""} </>
                  </Radio>
                </RadioGroup>
              </MenuItem>
            ))}
        <MenuDivider />
        <RadioGroup
          value={sortingOrder}
          onChange={(val: "asc" | "desc") => {
            setSortingOrder(val);
            handleSort({
              ordering: `${val === "asc" ? "" : "-"}${selectedSortingField}`,
            });
          }}
        >
          <MenuItem>
            <Radio value={"asc"}>Ascending</Radio>
          </MenuItem>
          <MenuItem>
            <Radio value={"desc"}>Descending</Radio>
          </MenuItem>
        </RadioGroup>
      </MenuList>
    </Menu>
  );
}

export default TableSort;
